import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { useSite } from '@/contexts/SiteContext';
import { Swords, Users, Building2, Globe, HelpCircle, RefreshCw, Trophy, ArrowRight, Clock, CheckCircle, XCircle, Timer, TrendingUp, TrendingDown, Minus, Share2, Megaphone } from 'lucide-react';
import {
  competitiveLandscapeService,
  type Actor,
  type CategoryBoardEntry,
  type LandscapeStats,
  type ActionEntry,
  type ActionsSummary,
  type AggregatorEntry,
  type InfluencerEntry
} from '@/services/competitiveLandscapeService';

// ============================================================================
// Constants
// ============================================================================

const ROLE_COLORS: Record<string, string> = {
  competitor: 'bg-red-100 text-red-800',
  brand: 'bg-blue-100 text-blue-800',
  aggregator: 'bg-orange-100 text-orange-800',
  information: 'bg-green-100 text-green-800',
  marketplace: 'bg-purple-100 text-purple-800',
  partner: 'bg-teal-100 text-teal-800',
  channel: 'bg-cyan-100 text-cyan-800',
  influencer: 'bg-pink-100 text-pink-800',
};

const TYPE_COLORS: Record<string, string> = {
  ecommerce: 'bg-red-50 text-red-700 border-red-200',
  brand: 'bg-blue-50 text-blue-700 border-blue-200',
  aggregator: 'bg-orange-50 text-orange-700 border-orange-200',
  marketplace: 'bg-purple-50 text-purple-700 border-purple-200',
  information: 'bg-green-50 text-green-700 border-green-200',
  forum: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  review: 'bg-lime-50 text-lime-700 border-lime-200',
  unknown: 'bg-gray-50 text-gray-500 border-gray-200',
};

const VALID_ROLES = ['competitor', 'brand', 'aggregator', 'information', 'marketplace', 'partner', 'channel', 'influencer'];

// ============================================================================
// Component
// ============================================================================

export default function CompetitiveLandscape() {
  const { selectedSite } = useSite();
  const siteId = selectedSite?.id || (selectedSite as any)?._id;
  const { toast } = useToast();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('summary');
  const [days, setDays] = useState(7);

  // Data states
  const [stats, setStats] = useState<LandscapeStats | null>(null);
  const [board, setBoard] = useState<CategoryBoardEntry[]>([]);
  const [actors, setActors] = useState<Actor[]>([]);
  const [roleFilter, setRoleFilter] = useState<string>('all');

  // Loading states
  const [statsLoading, setStatsLoading] = useState(true);
  const [boardLoading, setBoardLoading] = useState(false);
  const [actorsLoading, setActorsLoading] = useState(false);
  const [classifyLoading, setClassifyLoading] = useState(false);

  // Phase 4+5 states
  const [actions, setActions] = useState<ActionEntry[]>([]);
  const [actionsSummary, setActionsSummary] = useState<ActionsSummary | null>(null);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [aggregators, setAggregators] = useState<AggregatorEntry[]>([]);
  const [aggregatorsLoading, setAggregatorsLoading] = useState(false);
  const [influencers, setInfluencers] = useState<InfluencerEntry[]>([]);
  const [influencersLoading, setInfluencersLoading] = useState(false);

  // Fetch stats (Summary tab)
  const fetchStats = useCallback(async () => {
    if (!siteId) return;
    setStatsLoading(true);
    try {
      const data = await competitiveLandscapeService.getStats(siteId);
      setStats(data);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setStatsLoading(false);
    }
  }, [siteId, toast]);

  // Fetch category board
  const fetchBoard = useCallback(async () => {
    if (!siteId) return;
    setBoardLoading(true);
    try {
      const data = await competitiveLandscapeService.getCategoryBoard(siteId, days);
      setBoard(data.board);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setBoardLoading(false);
    }
  }, [siteId, days, toast]);

  // Fetch actors list
  const fetchActors = useCallback(async () => {
    if (!siteId) return;
    setActorsLoading(true);
    try {
      const filters = roleFilter !== 'all' ? { role: roleFilter } : undefined;
      const data = await competitiveLandscapeService.getActors(siteId, filters);
      setActors(data.actors);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setActorsLoading(false);
    }
  }, [siteId, roleFilter, toast]);

  // Fetch actions (Timeline tab)
  const fetchActions = useCallback(async () => {
    if (!siteId) return;
    setActionsLoading(true);
    try {
      const [actionsData, summaryData] = await Promise.all([
        competitiveLandscapeService.getActions(siteId, 90),
        competitiveLandscapeService.getActionsSummary(siteId, 30),
      ]);
      setActions(actionsData.actions);
      setActionsSummary(summaryData);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setActionsLoading(false);
    }
  }, [siteId, toast]);

  // Fetch aggregators
  const fetchAggregators = useCallback(async () => {
    if (!siteId) return;
    setAggregatorsLoading(true);
    try {
      const data = await competitiveLandscapeService.getAggregators(siteId);
      setAggregators(data.aggregators);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setAggregatorsLoading(false);
    }
  }, [siteId, toast]);

  // Fetch influencers
  const fetchInfluencers = useCallback(async () => {
    if (!siteId) return;
    setInfluencersLoading(true);
    try {
      const data = await competitiveLandscapeService.getInfluencers(siteId);
      setInfluencers(data.influencers);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setInfluencersLoading(false);
    }
  }, [siteId, toast]);

  // Trigger classification
  const handleClassify = async () => {
    if (!siteId) return;
    setClassifyLoading(true);
    try {
      const result = await competitiveLandscapeService.triggerClassification(siteId);
      toast({ title: 'Classification complete', description: `${result.actors} actors, ${result.classified} classified, ${result.roles} roles assigned` });
      fetchStats();
      if (activeTab === 'board') fetchBoard();
      if (activeTab === 'actors') fetchActors();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setClassifyLoading(false);
    }
  };

  // Role update
  const handleRoleUpdate = async (actorId: string, newRole: string) => {
    if (!siteId) return;
    try {
      await competitiveLandscapeService.updateActorRole(actorId, siteId, newRole);
      toast({ title: 'Role updated' });
      fetchActors();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Initial load
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (activeTab === 'board') fetchBoard(); }, [activeTab, fetchBoard]);
  useEffect(() => { if (activeTab === 'actors') fetchActors(); }, [activeTab, fetchActors]);
  useEffect(() => { if (activeTab === 'timeline') fetchActions(); }, [activeTab, fetchActions]);
  useEffect(() => { if (activeTab === 'aggregators') fetchAggregators(); }, [activeTab, fetchAggregators]);
  useEffect(() => { if (activeTab === 'influencers') fetchInfluencers(); }, [activeTab, fetchInfluencers]);

  if (!siteId) {
    return (
      <div className="p-6">
        <Alert><AlertDescription>Select a site to view the competitive landscape.</AlertDescription></Alert>
      </div>
    );
  }

  // Category grouping for Summary tab
  const leading = board.filter(c => c.ourPosition !== null && c.ourPosition <= 3);
  const competing = board.filter(c => c.ourPosition !== null && c.ourPosition > 3 && c.ourPosition <= 10);
  const behind = board.filter(c => c.ourPosition !== null && c.ourPosition > 10);
  const untapped = board.filter(c => c.ourPosition === null && c.demand && c.demand.commercialValue > 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Swords className="h-6 w-6" /> Competitive Landscape
          </h1>
          <p className="text-muted-foreground mt-1">Actors, categories, and your competitive position</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleClassify} disabled={classifyLoading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${classifyLoading ? 'animate-spin' : ''}`} />
            {classifyLoading ? 'Classifying...' : 'Run Classification'}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="board">Category Board</TabsTrigger>
          <TabsTrigger value="actors">Actors</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="aggregators">Aggregators</TabsTrigger>
          <TabsTrigger value="influencers">Influencers</TabsTrigger>
        </TabsList>

        {/* ============ SUMMARY TAB ============ */}
        <TabsContent value="summary" className="space-y-6">
          {statsLoading ? (
            <div className="grid grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
            </div>
          ) : stats ? (
            <>
              {/* Stat cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Total Actors</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{stats.totalActors}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Competitors</CardTitle></CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">
                      {stats.byRole.find(r => r.role === 'competitor')?.count || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Brands</CardTitle></CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {stats.byRole.find(r => r.role === 'brand')?.count || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Unclassified</CardTitle></CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-400">{stats.unclassified}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Role distribution */}
              <Card>
                <CardHeader><CardTitle className="text-sm">Actor Roles</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {stats.byRole.map(r => (
                      <Badge key={r.role} className={ROLE_COLORS[r.role] || 'bg-gray-100 text-gray-800'}>
                        {r.role}: {r.count}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Category position groups */}
              {board.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {leading.length > 0 && (
                    <Card className="border-green-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-green-600" /> Categories You Lead ({leading.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-1">
                          {leading.slice(0, 5).map(c => (
                            <div key={c.category} className="flex items-center justify-between text-sm">
                              <span className="truncate">{c.category}</span>
                              <Badge variant="outline" className="text-green-600">#{c.ourPosition}</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {competing.length > 0 && (
                    <Card className="border-yellow-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Swords className="h-4 w-4 text-yellow-600" /> Competing ({competing.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-1">
                          {competing.slice(0, 5).map(c => (
                            <div key={c.category} className="flex items-center justify-between text-sm">
                              <span className="truncate">{c.category}</span>
                              <Badge variant="outline" className="text-yellow-600">#{c.ourPosition}</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {behind.length > 0 && (
                    <Card className="border-orange-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Users className="h-4 w-4 text-orange-600" /> Behind ({behind.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-1">
                          {behind.slice(0, 5).map(c => (
                            <div key={c.category} className="flex items-center justify-between text-sm">
                              <span className="truncate">{c.category}</span>
                              <Badge variant="outline" className="text-orange-600">#{c.ourPosition}</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {untapped.length > 0 && (
                    <Card className="border-gray-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <HelpCircle className="h-4 w-4 text-gray-400" /> Untapped ({untapped.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-1">
                          {untapped.slice(0, 5).map(c => (
                            <div key={c.category} className="flex items-center justify-between text-sm">
                              <span className="truncate">{c.category}</span>
                              <span className="text-xs text-muted-foreground">
                                {c.demand?.commercialValue ? `€${Math.round(c.demand.commercialValue).toLocaleString()}/mo` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </>
          ) : (
            <Alert>
              <AlertDescription>No competitive data yet. Click "Run Classification" to discover and classify actors.</AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* ============ CATEGORY BOARD TAB ============ */}
        <TabsContent value="board" className="space-y-4">
          {boardLoading ? (
            <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12" />)}</div>
          ) : board.length > 0 ? (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Category</TableHead>
                    <TableHead>#1</TableHead>
                    <TableHead>#2</TableHead>
                    <TableHead>#3</TableHead>
                    <TableHead className="text-center">Our Position</TableHead>
                    <TableHead className="text-right">Demand Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {board.map(entry => (
                    <TableRow key={entry.category}>
                      <TableCell className="font-medium truncate max-w-[200px]">{entry.category}</TableCell>
                      {[0, 1, 2].map(idx => {
                        const actor = entry.actors[idx];
                        return (
                          <TableCell key={idx}>
                            {actor ? (
                              <div className="flex items-center gap-1">
                                <span className="text-sm truncate max-w-[120px]">{actor.domain}</span>
                                {actor.role && (
                                  <Badge variant="outline" className={`text-[10px] px-1 py-0 ${ROLE_COLORS[actor.role] || ''}`}>
                                    {actor.role[0].toUpperCase()}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center">
                        {entry.ourPosition ? (
                          <Badge className={
                            entry.ourPosition <= 3 ? 'bg-green-100 text-green-800' :
                            entry.ourPosition <= 10 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-orange-100 text-orange-800'
                          }>
                            #{entry.ourPosition}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">Not ranked</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {entry.demand?.commercialValue
                          ? `€${Math.round(entry.demand.commercialValue).toLocaleString()}`
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <Alert>
              <AlertDescription>No category metrics yet. Run classification and wait for the weekly aggregation, or check back after Sunday 04:00 UTC.</AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* ============ ACTORS TAB ============ */}
        <TabsContent value="actors" className="space-y-4">
          <div className="flex items-center gap-3">
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {VALID_ROLES.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {actorsLoading ? (
            <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10" />)}</div>
          ) : actors.length > 0 ? (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domain</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Relevance</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {actors.map(actor => (
                    <TableRow key={actor.id}>
                      <TableCell className="font-medium">{actor.domain}</TableCell>
                      <TableCell className="text-muted-foreground">{actor.name || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={TYPE_COLORS[actor.site_type] || TYPE_COLORS.unknown}>
                          {actor.site_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={actor.role || 'competitor'}
                          onValueChange={(v) => handleRoleUpdate(actor.id, v)}
                        >
                          <SelectTrigger className="w-[130px] h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {VALID_ROLES.map(r => (
                              <SelectItem key={r} value={r}>{r}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        {actor.relevance_score ? Math.round(actor.relevance_score) : 0}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/competitive-landscape/actor/${actor.id}`)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <Alert>
              <AlertDescription>No actors found. Run classification to discover and classify competitive actors.</AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* ============ TIMELINE TAB ============ */}
        <TabsContent value="timeline" className="space-y-6">
          {actionsLoading ? (
            <div className="space-y-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-16" />)}</div>
          ) : (
            <>
              {/* Summary cards */}
              {actionsSummary && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Actions (30d)</CardTitle></CardHeader>
                    <CardContent><div className="text-2xl font-bold">{actionsSummary.totalActions}</div></CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Positive</CardTitle></CardHeader>
                    <CardContent><div className="text-2xl font-bold text-green-600">{actionsSummary.positive}</div></CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Neutral</CardTitle></CardHeader>
                    <CardContent><div className="text-2xl font-bold text-gray-500">{actionsSummary.neutral}</div></CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Pending</CardTitle></CardHeader>
                    <CardContent><div className="text-2xl font-bold text-yellow-600">{actionsSummary.pending + actionsSummary.tooEarly}</div></CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Avg Time to Impact</CardTitle></CardHeader>
                    <CardContent><div className="text-2xl font-bold">{actionsSummary.avgTimeToImpactDays ? `${actionsSummary.avgTimeToImpactDays}d` : '—'}</div></CardContent>
                  </Card>
                </div>
              )}

              {/* Actions list */}
              {actions.length > 0 ? (
                <div className="space-y-3">
                  {actions.map(action => (
                    <Card key={action.id} className={
                      action.impactStatus === 'positive' ? 'border-green-200' :
                      action.impactStatus === 'negative' ? 'border-red-200' :
                      action.impactStatus === 'pending' || action.impactStatus === 'too_early' ? 'border-yellow-200' :
                      'border-gray-200'
                    }>
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {action.impactStatus === 'positive' && <CheckCircle className="h-5 w-5 text-green-600" />}
                            {action.impactStatus === 'negative' && <XCircle className="h-5 w-5 text-red-600" />}
                            {action.impactStatus === 'neutral' && <Minus className="h-5 w-5 text-gray-400" />}
                            {(action.impactStatus === 'pending' || action.impactStatus === 'too_early') && <Timer className="h-5 w-5 text-yellow-500" />}
                            <div>
                              <div className="text-sm font-medium">
                                {action.type === 'deployment' ? 'Content Deployed' : 'Product Enrichment'}
                                {action.categoryPath && <span className="text-muted-foreground ml-2">· {action.categoryPath}</span>}
                              </div>
                              <div className="text-xs text-muted-foreground truncate max-w-[400px]">
                                {action.url || 'No URL'}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            {action.positionDelta != null && (
                              <div className="flex items-center gap-1">
                                {action.positionDelta > 0 ? (
                                  <TrendingUp className="h-4 w-4 text-green-600" />
                                ) : action.positionDelta < 0 ? (
                                  <TrendingDown className="h-4 w-4 text-red-600" />
                                ) : null}
                                <span className={action.positionDelta > 0 ? 'text-green-600' : action.positionDelta < 0 ? 'text-red-600' : ''}>
                                  {action.positionBefore?.toFixed(1)} → {action.positionAfter?.toFixed(1)}
                                </span>
                              </div>
                            )}
                            {action.sovDeltaPct != null && (
                              <div className="text-xs">
                                SOV: {action.sovBeforePct?.toFixed(1)}% → {action.sovAfterPct?.toFixed(1)}%
                              </div>
                            )}
                            <Badge variant="outline" className={
                              action.impactStatus === 'positive' ? 'text-green-700 border-green-300' :
                              action.impactStatus === 'negative' ? 'text-red-700 border-red-300' :
                              action.impactStatus === 'pending' || action.impactStatus === 'too_early' ? 'text-yellow-700 border-yellow-300' :
                              'text-gray-500 border-gray-300'
                            }>
                              {action.impactStatus === 'too_early' ? 'Too early' : action.impactStatus}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(action.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Alert>
                  <AlertDescription>No actions detected yet. Actions are auto-detected daily from deployments and enrichments.</AlertDescription>
                </Alert>
              )}
            </>
          )}
        </TabsContent>

        {/* ============ AGGREGATORS TAB ============ */}
        <TabsContent value="aggregators" className="space-y-4">
          {aggregatorsLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10" />)}</div>
          ) : aggregators.length > 0 ? (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Platform</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-center">Categories Present</TableHead>
                    <TableHead className="text-center">Categories Missing</TableHead>
                    <TableHead className="text-center">Authority</TableHead>
                    <TableHead className="text-center">SOV %</TableHead>
                    <TableHead className="text-right">Mentions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aggregators.map(agg => (
                    <TableRow key={agg.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{agg.domain}</div>
                          {agg.name && <div className="text-xs text-muted-foreground">{agg.name}</div>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-orange-50 text-orange-700">{agg.role}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-green-700 border-green-300">{agg.categoriesPresent}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {agg.categoriesMissing > 0 ? (
                          <Badge variant="outline" className="text-orange-700 border-orange-300">{agg.categoriesMissing}</Badge>
                        ) : (
                          <span className="text-green-600 text-sm">All covered</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {agg.avgAuthority != null ? agg.avgAuthority.toFixed(1) : '—'}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {agg.avgSovPct != null ? `${agg.avgSovPct.toFixed(1)}%` : '—'}
                      </TableCell>
                      <TableCell className="text-right text-sm">{agg.totalMentions}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <Alert>
              <AlertDescription>No aggregators or marketplaces found in your competitive landscape yet.</AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* ============ INFLUENCERS TAB ============ */}
        <TabsContent value="influencers" className="space-y-4">
          {influencersLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10" />)}</div>
          ) : influencers.length > 0 ? (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-center">Categories</TableHead>
                    <TableHead className="text-center">Authority</TableHead>
                    <TableHead className="text-center">SOV %</TableHead>
                    <TableHead className="text-center">AI Providers</TableHead>
                    <TableHead className="text-right">Mentions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {influencers.map(inf => (
                    <TableRow key={inf.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{inf.domain}</div>
                          {inf.name && <div className="text-xs text-muted-foreground">{inf.name}</div>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-green-50 text-green-700">{inf.siteType}</Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm">{inf.categoriesActive}</TableCell>
                      <TableCell className="text-center text-sm">
                        {inf.avgAuthority != null ? inf.avgAuthority.toFixed(1) : '—'}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {inf.avgSovPct != null ? `${inf.avgSovPct.toFixed(1)}%` : '—'}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex gap-1 justify-center">
                          {inf.providers.map(p => (
                            <Badge key={p} variant="outline" className="text-[10px] px-1 py-0">
                              {p === 'openai' ? 'GPT' : p === 'anthropic' ? 'Claude' : p === 'perplexity' ? 'Pplx' : p === 'google' ? 'Gem' : p}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm">{inf.totalMentions}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <Alert>
              <AlertDescription>No information sources or influencers found in your competitive landscape yet.</AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
